import { Code, Server, Layers } from "lucide-react"
import PythonLogo from "./icons/Python"
import JavascriptLogo from "./icons/JavaScript"
import TypescriptLogo from "./icons/Typescript";
import RustLogo from "./icons/Rust";
import ReactLogo from "./icons/React";
import ZigLogo from "./icons/Zig";
import GodotLogo from "./icons/Godot";

interface Language {
  name: string
  icon: string
  proficiency: string
  description: string
}

interface ExperienceSectionProps {
  languages: Language[]
}

export default function ExperienceSection({ languages }: ExperienceSectionProps) {

  const renderIcon = (iconName: string) => {
    const iconProps = {
      size: 35,
      color: "turquoise",
      strokeWidth: 5
    }

    switch (iconName.toLowerCase()) {
      case "typescript":
        return <TypescriptLogo {...iconProps} />
      case "javascript":
        return <JavascriptLogo {...iconProps} />
      case "rust":
        return <RustLogo {...iconProps} />
      case "go":
        return <RustLogo {...iconProps} />
      case "python":
        return <PythonLogo {...iconProps} strokeWidth={1} />
      case "react":
        return <ReactLogo {...iconProps} />
      case "zig":
        return <ZigLogo {...iconProps} strokeWidth={1} />
      case "gdscript":
        return <GodotLogo {...iconProps} />
      case "backend":
        return <Server {...iconProps} />
      case "frontend":
        return <Layers {...iconProps} />
      case "default":
      case "code":
        return <Code {...iconProps} />
      default:
        return <Code {...iconProps} />
    }
  }

  const getProficiencyWidth = (proficiency: string) => {
    switch (proficiency.toLowerCase()) {
      case "advanced":
        return "w-full"
      case "intermediate":
        return "w-2/3"
      case "beginner":
        return "w-1/3"
      default:
        return "w-1/2"
    }
  }


  return (
    <div className="mb-16 mx-2 md:w-1/2 w-full">
      <div className="bg-background border border-turquoise/10 rounded-xl p-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute inset-[1px] rounded-xl [background:linear-gradient(45deg,#0a0a0a,theme(colors.background)_50%,#0a0a0a)_padding-box,conic-gradient(from_var(--border-angle),theme(colors.contentBorder)_80%,_theme(colors.turquoise.DEFAULT)_86%,_theme(colors.turquoise.light)_90%,_theme(colors.turquoise.DEFAULT)_94%,_theme(colors.contentBorder))_border-box] border border-transparent animate-border"></div>
        </div>

        <div className="relative z-10 space-y-6 max-h-[410px] overflow-scroll">
          {languages.map((language, index) => (
            <div key={index} className="flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {renderIcon(language.icon)}
                  <h4 className="text-md font-medium text-text">{language.name}</h4>
                </div>
              </div>

              <div className="h-1 w-full bg-turquoise/5 rounded-full mb-2">
                <div
                  className={`h-full rounded-full ${getProficiencyWidth(language.proficiency)}`}
                  style={{
                    backgroundImage: 'linear-gradient(to right, #0F6B63, turquoise, #4ECDC4)',
                  }}
                  title={`${language.proficiency} proficiency`}
                ></div>
              </div>

              <p className="text-gray-300 text-sm">{language.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
